import React, { useState, useEffect } from 'react';
import {
    Alert,
    Box,
    Button,
    Typography,
    Chip,
    CircularProgress,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-okaidia.css';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import jsonLang from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import xmlLang from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import axios from 'axios';
import { restCalcRequest } from '@carboncare/schemas';

SyntaxHighlighter.registerLanguage('json', jsonLang);
SyntaxHighlighter.registerLanguage('xml', xmlLang);

// Direct XML/REST microservice URLs — same pattern as XmlApiForm
// XML API key is embedded in the payload body (<ApiKey>…</ApiKey>), no auth header needed
const XML_CALC_URL = process.env.REACT_APP_XML_API_URL || 'http://localhost:8080/xml/calc';
const REST_CALC_URL = XML_CALC_URL.replace('/xml/calc', '/rest/calc');

export interface PayloadSample {
    json: string;
    xml: string;
}

interface Props {
    samples: PayloadSample;
    apiKey: string;
}

// Strip // comments and trailing commas, then parse. Pure: returns parsed data or null.
function parseJsonPayload(payload: string): { data: any } | null {
    try {
        const cleanJson = payload
            .replaceAll(/\/\/.{0,2000}$/gm, '')     // remove comment lines
            .replaceAll(/,(\s*[}\]])/g, '$1')      // remove trailing commas
            .trim();
        return { data: JSON.parse(cleanJson) };
    } catch {
        return null;
    }
}

// Replace the API-key placeholder in an XML body (longest form first so
// YOUR_API_KEY_HERE doesn't become "<key>_HERE"). Pure.
function injectXmlKey(payload: string, trimmedKey: string): string {
    return payload.includes('YOUR_API_KEY')
        ? payload.replaceAll('YOUR_API_KEY_HERE', trimmedKey).replaceAll('YOUR_API_KEY', trimmedKey)
        : payload; // user manually typed their own key — send as-is
}

// Map a parsed payload to schema-deviation messages (empty when valid). Pure.
function schemaIssuesFor(data: any): string[] {
    const check = restCalcRequest.safeParse(data);
    return check.success
        ? []
        : check.error.issues.map(i => `${i.path.join('.') || '(root)'}: ${i.message}`);
}

const PayloadExecutor: React.FC<Props> = ({ samples, apiKey }) => {
    const [format, setFormat] = useState<'json' | 'xml'>('json');
    const [payload, setPayload] = useState<string>(samples.json);
    const [response, setResponse] = useState<string | null>(null);
    const [status, setStatus] = useState<number | null>(null);
    const [executing, setExecuting] = useState(false);
    const [schemaIssues, setSchemaIssues] = useState<string[]>([]);

    // When format changes switch to the matching sample (never inject API key into editor)
    useEffect(() => {
        setPayload(format === 'json' ? samples.json : samples.xml);
        setResponse(null);
        setStatus(null);
        setSchemaIssues([]);
    }, [format]); // eslint-disable-line react-hooks/exhaustive-deps

    // When samples prop changes (different section) reset to JSON
    useEffect(() => {
        setFormat('json');
        setPayload(samples.json);
        setResponse(null);
        setStatus(null);
        setSchemaIssues([]);
    }, [samples]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleExecute = async () => {
        setExecuting(true);
        setResponse(null);
        setStatus(null);
        try {
            const isJson = format === 'json';
            const endpoint = isJson ? REST_CALC_URL : XML_CALC_URL;

            let data: any;
            if (isJson) {
                const parsed = parseJsonPayload(payload);
                if (!parsed) {
                    setStatus(400);
                    setResponse('Invalid JSON — check your payload syntax.');
                    setExecuting(false);
                    return;
                }
                data = parsed.data;
                // Preflight against the shared contract schema — warn, never block
                setSchemaIssues(schemaIssuesFor(data));
            } else {
                // XML: inject the real API key silently — never expose it in the editor
                const trimmedKey = (apiKey || '').trim();
                if (!trimmedKey) {
                    setStatus(401);
                    setResponse('No API key available. Please ensure your account has a valid API key configured.');
                    setExecuting(false);
                    return;
                }
                data = injectXmlKey(payload, trimmedKey);
            }

            // JSON: key sent as Authorization header (not in payload)
            // XML: key injected into payload body above; also send as header for redundancy
            const res = await axios.post(endpoint, data, {
                headers: {
                    'Content-Type': isJson ? 'application/json' : 'application/xml',
                    ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
                },
                validateStatus: () => true,
            });


            setStatus(res.status);
            setResponse(
                isJson && typeof res.data === 'object'
                    ? JSON.stringify(res.data, null, 2)
                    : String(res.data)
            );
        } catch (e: any) {
            setStatus(500);
            setResponse(e.message);
        } finally {
            setExecuting(false);
        }
    };

    const isError = status !== null && status >= 400;
    const labelColor = format === 'json' ? '#89b4fa' : '#fab387';

    return (
        <Box>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup
                    value={format}
                    exclusive
                    onChange={(_, v) => v && setFormat(v as 'json' | 'xml')}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            px: 2,
                            fontSize: '0.8rem',
                        },
                    }}
                >
                    <ToggleButton value="json">REST / JSON</ToggleButton>
                    <ToggleButton value="xml">XML / SOAP</ToggleButton>
                </ToggleButtonGroup>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExecute}
                    disabled={executing}
                    startIcon={executing ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                    sx={{ textTransform: 'none', px: 2.5, py: 0.75, borderRadius: 2, fontSize: '0.83rem', fontWeight: 600 }}
                >
                    {executing ? 'Executing…' : 'Execute Request'}
                </Button>
            </Box>

            {schemaIssues.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5, fontSize: '0.8rem' }} onClose={() => setSchemaIssues([])}>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                        Payload deviates from the API schema (request was still sent):
                    </Typography>
                    {schemaIssues.slice(0, 8).map(issue => (
                        <Typography key={issue} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                            • {issue}
                        </Typography>
                    ))}
                    {schemaIssues.length > 8 && (
                        <Typography variant="caption" component="div">…and {schemaIssues.length - 8} more</Typography>
                    )}
                </Alert>
            )}

            {/* Editor + Response side-by-side */}
            <Grid container sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>

                {/* Left — Editable Payload */}
                <Grid
                    item xs={12} lg={6}
                    sx={{
                        borderRight: { lg: '1px solid #333' },
                        borderBottom: { xs: '1px solid #333', lg: 'none' },
                    }}
                >
                    <Box sx={{ px: 1.5, py: 1, bgcolor: '#1e1e2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: labelColor, fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                            {format === 'json' ? 'JSON Payload' : 'XML Payload'}
                        </Typography>
                        <Chip label="Editable" size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }} />
                    </Box>
                    <Box sx={{ bgcolor: '#282a36', overflow: 'auto', maxHeight: 360 }}>
                        <Editor
                            value={payload}
                            onValueChange={setPayload}
                            highlight={code =>
                                Prism.highlight(
                                    code,
                                    format === 'json' ? Prism.languages.json : Prism.languages.markup,
                                    format === 'json' ? 'json' : 'markup'
                                )
                            }
                            padding={14}
                            style={{
                                fontFamily: '"Fira Code", "Fira Mono", monospace',
                                fontSize: '12.5px',
                                color: '#f8f8f2',
                                minHeight: 200,
                            }}
                        />
                    </Box>
                </Grid>

                {/* Right — Response */}
                <Grid
                    item xs={12} lg={6}
                    sx={{ bgcolor: isError ? '#2d1b1b' : '#1e1e2e', transition: 'background-color 0.3s' }}
                >
                    <Box sx={{ px: 1.5, py: 1, bgcolor: isError ? '#3d1f1f' : '#181825', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: isError ? '#f38ba8' : '#a6e3a1', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                            Response
                        </Typography>
                        {status !== null && (
                            <Chip
                                label={`HTTP ${status}`}
                                size="small"
                                sx={{
                                    fontSize: '0.65rem', height: 18, fontWeight: 700,
                                    bgcolor: isError ? 'rgba(243,139,168,0.15)' : 'rgba(166,227,161,0.15)',
                                    color: isError ? '#f38ba8' : '#a6e3a1',
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ overflow: 'auto', maxHeight: 360, minHeight: 200, display: 'flex', alignItems: response ? 'flex-start' : 'center', justifyContent: response ? 'flex-start' : 'center' }}>
                        {response ? (
                            <SyntaxHighlighter
                                language={format === 'json' ? 'json' : 'xml'}
                                style={vs2015}
                                customStyle={{ margin: 0, padding: '14px', borderRadius: 0, fontSize: '12.5px', backgroundColor: 'transparent', width: '100%' }}
                                wrapLongLines
                            >
                                {response}
                            </SyntaxHighlighter>
                        ) : (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.8rem', p: 2, textAlign: 'center' }}>
                                Hit "Execute Request" to see the live response.
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PayloadExecutor;
